import { Button } from "primereact/button";
import { useEffect, useState, useRef } from "react";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import "./filter.css";
import oilProductionService from "../../../services/oilProduction/oilProductionservice";

interface SearchPanelProps {
  onFilter: (query: any) => void;
  onReset: () => void;
}

const BullCardFilter: React.FC<SearchPanelProps> = ({ onFilter, onReset }) => {
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    return `${year}-${month}`;
  };

  const toast = useRef<Toast>(null);
  const [manufacturingUnits, setManufacturingUnits] = useState<any[]>([]);
  const [bullList, setBullList] = useState<any[]>([]);
  const [manufacturingUnitId, setManufacturingUnitId] = useState<number>(0);
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());
  const [bullId, setBullId] = useState<number | null>(null);
  const [masterBullList, setMasterBullList] = useState<any[]>([]);

  useEffect(() => {
    const fetchBullMasterData = async () => {
      try {
        const bcMasterData =
          await oilProductionService.getBullStatisticsMasterDataAsync();

        setManufacturingUnits(bcMasterData.manufacturingUnitList);
        setMasterBullList(bcMasterData.bullList);
      } catch (err) {
        console.error("Error fetching bull master data:", err);
      }
    };
    fetchBullMasterData();
  }, []);

  function handleFilter() {
    if (!manufacturingUnitId || !selectedMonth) {
      toast.current?.show({
        severity: "info",
        summary: "Info",
        detail:
          "Please select the Manufacturing Unit and Month before filtering.",
        life: 3000,
      });
      return;
    }

    const selMY = selectedMonth.split("-");
    const year = parseInt(selMY[0]);
    const month = parseInt(selMY[1]);

    const data = {
      manufacturingUnitId: manufacturingUnitId,
      month: month,
      year: year,
      bullId: bullId ?? "",
    };
    onFilter(data);
  }

  function handleReset() {
    setManufacturingUnitId(0);
    setSelectedMonth(getCurrentMonth());
    setBullId(null);
    onReset();
  }

  function handleChangeManufacturingUnit(id: number) {
    setBullId(null);
    setManufacturingUnitId(id);
    setBullList(
      masterBullList.filter((bull) => bull.manufacturingUnitId === id)
    );
  }

  return (
    <div>
      <Toast ref={toast} />
      <div className="filter-bar-container">
        <div className="filter-grid">
          {/* Manufacturing Unit Dropdown */}
          <div className="filter-field">
            <label className="filter-label">
              <i className="pi pi-building" />
              <span>Manufacturing Unit</span>
              <span className="required">*</span>
            </label>
            <Dropdown
              value={manufacturingUnitId}
              options={manufacturingUnits}
              optionValue="id"
              optionLabel="name"
              placeholder="Select Manufacturing Unit"
              onChange={(eve) => handleChangeManufacturingUnit(eve.value)}
              className="filter-dropdown"
              pt={{
                root: { className: "filter-dropdown" },
              }}
            />
          </div>

          {/* Month Calendar */}
          <div className="filter-field">
            <label className="filter-label">
              <i className="pi pi-calendar" />
              <span>Month</span>
              <span className="required">*</span>
            </label>
            <Calendar
              value={selectedMonth ? new Date(selectedMonth + "-01") : null}
              onChange={(e) => {
                if (e.value) {
                  const selectedDate = e.value as Date;
                  const year = selectedDate.getFullYear();
                  const monthValue = (selectedDate.getMonth() + 1)
                    .toString()
                    .padStart(2, "0");
                  const formattedMonth = `${year}-${monthValue}`;
                  setSelectedMonth(formattedMonth);
                }
              }}
              view="month"
              dateFormat="M, yy"
              maxDate={new Date()}
              showIcon
              className="filter-calendar"
              pt={{
                root: { className: "filter-calendar" },
              }}
            />
          </div>

          {/* Bull Dropdown */}
          <div className="filter-field">
            <label className="filter-label">
              <i className="pi pi-tag" />
              <span>Bull</span>
            </label>
            <Dropdown
              value={bullId}
              options={bullList}
              optionValue="id"
              optionLabel="name"
              placeholder="Select Bull"
              onChange={(e) => setBullId(e.value)}
              className="filter-dropdown"
              disabled={!manufacturingUnitId}
              showClear
              pt={{
                root: { className: "filter-dropdown" },
              }}
            />
          </div>

          {/* Action Buttons */}
          <div className="filter-actions">
            <Button
              label="Filter"
              icon="pi pi-filter"
              onClick={handleFilter}
              className="filter-btn"
              pt={{
                root: { className: "filter-btn" },
              }}
            />
            <Button
              label="Reset"
              icon="pi pi-times"
              onClick={handleReset}
              className="reset-btn"
              pt={{
                root: { className: "reset-btn" },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BullCardFilter;
